import { useMemo, useState } from "react";
import date from "date-and-time";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { Dialog } from "@headlessui/react";
import { DocumentArrowDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import CustomDatePicker from "../../../awaitingInventory/CustomDatePicker";
import useLeadsForExport from "../../../../data/useLeadsForExport";
import { LeadStatus } from "../../../../models/Lead";
import toIsoDate from "../../../../utils/toIsoDate";
import JSONToCSVExporter from "./JSONToCSVExporter.js";

const displayColumnNames =
["CollectID", "CCID", "Atf Reference Number", "Status", "Locked", "Source", "Enquiry Date", "Customer_Accepted", "Earliest Contact", "Latest Contact", "Earliest Collect", "Latest Collect", "ATF_Date_Accept", "Date_Confirmed", "Date_Accepted", "VRM", "Make", "Model", "Category", "VIN", "Fuel_Type", "Year_First_Registered", "Mileage", "Remaining MOT", "Service History", "Bodywork", "Interior", "Mechanical", "Overall Condition", "Reserve", "VAT_Applicable", "VehicleID", "CTBID", "Location Title", "Location First Name", "Location Last Name", "Location Reference", "Location Organisation", "Location Building", "Location Street", "Location Locality", "Location Town", "Location County", "Location Postcode", "Location Telephone (main)", "Location Telephone (alternative)", "Location Email", "Customer Title", "Customer First Name", "Customer Last Name", "Customer Organisation", "Customer Building", "Customer Street", "Customer Locality", "Customer Town", "Customer County", "Customer Postcode", "Customer Telephone (main)", "Customer Telephone (alternative)", "Customer Email", "Customer Group", "Central Payment", "Hide Price", "Advert Reference", "Other Advert Reference", "Starts and Drives", "Area", "Service", "Method", "Operator", "Sales Price", "Referral Fee", "Payment Type", "Account Name", "Account Number", "Sort Code", "Payment Reference", "Card Number", "Payee DOB", "Cheque Name", "Cheque Number", "Premium Price Used", "Scheme", "Vehicle Grade"];
const columnNames =
["CollectId", "ccid", "Atf Reference Number", "status", "Locked", "source", "created", "Customer_Accepted", "Earliest Contact", "Latest Contact", "vehicle.collectedOn", "Latest Collect", "ATF_Date_Accept", "Date_Confirmed", "Date_Accepted", "vehicle.vrm", "vehicle.combinedMake", "vehicle.combinedModel", "vehicle.vehicleCategoryDescription", "vehicle.combinedVIN", "vehicle.fuelType", "vehicle.dateFirstRegistered", "Mileage", "Remaining MOT", "Service History", "Bodywork", "Interior", "Mechanical", "Overall Condition", "Reserve", "applyVAT", "vehicle.id", "CTBID", "Location Title", "vehicle.fullname", "Location Last Name", "Location Reference", "vehicle.organisation", "vehicle.line1", "vehicle.line2", "vehicle.locality", "vehicle.town", "vehicle.county", "vehicle.postcode", "vehicle.telephone1", "vehicle.telephone2", "vehicle.email", "Customer Title", "contact.fullname", "Customer Last Name", "contact.organisation", "contact.line1", "contact.line2", "Customer Locality", "contact.town", "contact.county", "contact.postcode", "contact.telephone1", "contact.telephone2", "contact.email", "Customer Group", "Central Payment", "Hide Price", "Advert Reference", "Other Advert Reference", "Starts and Drives", "Area", "Service", "Method", "Operator", "price", "commission", "paymentType", "accountName", "accountNumber", "sortCode", "paymentReference", "cardNumber", "payeeDOB", "chequeName", "chequeNumber", "premiumPriceUsed", "scheme", "vehicleGrade"];

// The export used to fetch every lead in the system and throw away all but the unexported ones in
// the browser. A default window keeps that first request small; a yard that has not exported for
// longer than this just widens the range.
const DEFAULT_RANGE_DAYS = 30;

export interface ExportContactsProps {
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
  onExport: React.MouseEventHandler<HTMLButtonElement>;
}

export default function ExportLeads({ onCancel, onExport, }: ExportContactsProps) {
  const [dates, setDates] = useState<DateRangeType>({
    startDate: date.addDays(new Date(), -DEFAULT_RANGE_DAYS),
    endDate: new Date(),
  });
  const [includeExported, setIncludeExported] = useState(false);

  // Nothing is fetched until Lookup is pressed, so opening the modal costs no query and a
  // half-typed range never hits the API. Changing either control clears this, because a count
  // fetched for the previous range would no longer describe what the controls say.
  const [criteria, setCriteria] = useState<{
    startDate: string;
    endDate: string;
    includeExported: boolean;
  } | null>(null);

  const startDate = dates.startDate ? toIsoDate(dates.startDate) : null;
  const endDate = dates.endDate ? toIsoDate(dates.endDate) : null;
  const hasRange = !!startDate && !!endDate;

  const { leadsForExport } = useLeadsForExport(
    {
      startDate: criteria?.startDate ?? null,
      endDate: criteria?.endDate ?? null,
      includeExported: criteria?.includeExported ?? false,
    },
    "ExportLeads"
  );

  const handleDatesChange = (next: DateRangeType) => {
    setDates(next);
    setCriteria(null);
  };

  const handleIncludeExportedChange = (next: boolean) => {
    setIncludeExported(next);
    setCriteria(null);
  };

  const runLookup = () => {
    if (!startDate || !endDate) return;
    setCriteria({ startDate, endDate, includeExported });
  };

  // Process the status enum into a string so the CSV reads as it does on screen. The API has
  // already applied the date range, the exported filter and the vehicle-not-found filter.
  const formattedLeads = useMemo(() => {
    const leads = leadsForExport.data?.data.leads;
    if (!leads) return [];

    return leads.map((lead) => ({
      ...lead,
      status:
        typeof lead.status === "number"
          ? LeadStatus[lead.status] ?? lead.status
          : lead.status,
    })) as Record<string, unknown>[];
  }, [leadsForExport.data?.data.leads]);

  const lastExported = leadsForExport.data?.data.lastExported;

  return (
    <>
      <div className="sm:flex sm:items-center">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
          <DocumentArrowDownIcon
            className="h-6 w-6 text-primary-600"
            aria-hidden="true"
          />
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            Export Leads to CSV
          </Dialog.Title>
        </div>
      </div>
      <div>
        <div className="my-8">
          Choose the enquiry date range to export, then press Lookup. Only leads raised in that
          range are fetched, and by default only the ones that haven't been exported yet.
          <div className="my-6 flex items-start gap-x-3">
            <CustomDatePicker dates={dates} handleDatesChange={handleDatesChange} />
            <button
              type="button"
              disabled={!hasRange || leadsForExport.isFetching}
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={runLookup}
            >
              <MagnifyingGlassIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              {leadsForExport.isFetching ? "Looking up..." : "Lookup"}
            </button>
          </div>
          <label className="flex items-center gap-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={includeExported}
              onChange={(e) => handleIncludeExportedChange(e.target.checked)}
            />
            Include leads that have already been exported
          </label>
          {lastExported && (
            <div className="mt-6 text-sm text-gray-500">
              Last export: {date.format(new Date(lastExported), "DD/MM/YY HH:mm")}
            </div>
          )}
        </div>
        <div className="mt-10 text-center">
          {criteria ? (
            <JSONToCSVExporter
              displayColumnNames={displayColumnNames}
              columnNames={columnNames}
              jsonData={formattedLeads}
              isLoading={leadsForExport.isFetching}
            />
          ) : (
            <p className="text-lg text-gray-500">
              {hasRange ? "Press Lookup to count the leads in this range" : "Pick a date range to export"}
            </p>
          )}
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </>
  );
}
